import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, Calendar, DollarSign, TrendingUp, Info, ChevronRight, Sparkles, MessageCircle, Download, RotateCcw, Briefcase, ShieldAlert, Scale, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { calcRescisao, formatBRL } from '../lib/calcRescisao';
import { firmsApi } from '../api/client';
import SEO from '../components/SEO';
import LawFirmCard from '../components/LawFirmCard';

const FAQS = [
  {
    q: 'Como é calculada a multa de 40% do FGTS?',
    a: 'A multa incide sobre o total depositado durante todo o contrato (não apenas o saldo atual). Em caso de acordo (art. 484-A da CLT), a multa cai para 20%. Em pedido de demissão ou justa causa, não há multa.',
  },
  {
    q: 'O que é aviso prévio proporcional?',
    a: 'Desde a Lei 12.506/2011, o aviso prévio é de 30 dias + 3 dias adicionais para cada ano completo de serviço, limitado a 90 dias no total. Ele pode ser trabalhado ou indenizado.',
  },
  {
    q: 'Tenho direito ao 13º proporcional?',
    a: 'Sim, em todas as modalidades exceto justa causa. Considera-se mês integral quando você trabalhou 15 dias ou mais naquele mês.',
  },
  {
    q: 'Posso sacar o FGTS após pedir demissão?',
    a: 'Não. No pedido de demissão, você não tem direito ao saque do FGTS nem à multa de 40%. No acordo entre as partes, você pode sacar até 80% do saldo.',
  },
  {
    q: 'Esse cálculo tem validade jurídica?',
    a: 'É apenas uma estimativa. Valores finais dependem de horas extras, adicionais noturnos, insalubridade, comissões, descontos previdenciários e IR. Um advogado trabalhista pode calcular com precisão e cobrar diferenças.',
  },
];

export default function RescissionCalculator() {
  const [form, setForm] = useState({
    salary: '',
    admission: '',
    dismissal: new Date().toISOString().split('T')[0],
    reason: 'sem-justa-causa',
    noticeType: 'indenizado',
    fgtsBalance: '',
    vacationOverdue: '0',
  });
  const [showResult, setShowResult] = useState(false);
  const [trabFirms, setTrabFirms] = useState([]);

  const result = useMemo(() => {
    if (!showResult) return null;
    return calcRescisao({
      ...form,
      salary: form.salary,
      fgtsBalance: form.fgtsBalance,
      vacationOverdue: form.vacationOverdue,
    });
  }, [form, showResult]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    setShowResult(true);
    // fetch top trabalhista lawyers for CTA
    firmsApi.list({ category: 'trabalhista', sort: 'rating', limit: 3 })
      .then(d => setTrabFirms(d.items))
      .catch(() => {});
    setTimeout(() => document.getElementById('resultado')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const reset = () => {
    setShowResult(false);
    setForm({ salary: '', admission: '', dismissal: new Date().toISOString().split('T')[0], reason: 'sem-justa-causa', noticeType: 'indenizado', fgtsBalance: '', vacationOverdue: '0' });
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(f => ({
      '@type': 'Question', name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <div>
      <SEO
        title="Calculadora de rescisão trabalhista grátis"
        description="Simulador gratuito de rescisão trabalhista. Calcule aviso prévio, 13º proporcional, férias, multa do FGTS e valor total a receber. Baseado na CLT e Reforma Trabalhista."
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative bg-white border-b border-[#cbd8dc] text-[#122f36] overflow-hidden">
        <div className="aj-wrap max-w-[820px] py-14 md:py-16 relative">
          <div className="inline-flex items-center gap-2 bg-[#edf4f5] border border-[#cbd8dc] rounded-full px-4 py-1.5 text-xs font-medium text-[#075a6d] mb-4">
            <Calculator className="w-3.5 h-3.5" /> Simulador 100% gratuito
          </div>
          <h1 className="text-3xl md:text-5xl font-semibold leading-tight">Calculadora de <span className="text-[#075a6d]">rescisão trabalhista</span></h1>
          <p className="mt-4 text-lg text-[#354c54] max-w-2xl leading-relaxed">Descubra em segundos quanto você deve receber na sua rescisão. Calculamos aviso prévio, 13º proporcional, férias, multa do FGTS e todas as verbas rescisórias com base na CLT.</p>
        </div>
      </section>

      <div className="aj-wrap max-w-[820px] mt-8 relative pb-16">
        {/* Form */}
        <form onSubmit={submit} className="bg-white rounded-[10px] border border-[#cbd8dc] p-6 md:p-8">
          <h2 className="text-lg font-semibold text-[#153e44] mb-5 flex items-center gap-2"><Briefcase className="w-5 h-5 text-[#075a6d]" /> Dados do contrato</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <Label className="text-xs font-semibold text-[#354c54] uppercase tracking-wider">Salário bruto mensal</Label>
              <div className="relative mt-1.5">
                <DollarSign className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6b858e]" />
                <Input type="number" required min="1" step="0.01" value={form.salary} onChange={(e) => set('salary', e.target.value)} placeholder="Ex: 3500" className="pl-9 h-11" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#354c54] uppercase tracking-wider">Saldo total do FGTS depositado (opcional)</Label>
              <div className="relative mt-1.5">
                <DollarSign className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6b858e]" />
                <Input type="number" min="0" step="0.01" value={form.fgtsBalance} onChange={(e) => set('fgtsBalance', e.target.value)} placeholder="Ex: 4200" className="pl-9 h-11" />
              </div>
              <p className="text-xs text-[#52676e] mt-1">Consulte no app FGTS ou extrato da Caixa. Se não souber, deixe em branco.</p>
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#354c54] uppercase tracking-wider">Data de admissão</Label>
              <div className="relative mt-1.5">
                <Calendar className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6b858e]" />
                <Input type="date" required value={form.admission} onChange={(e) => set('admission', e.target.value)} className="pl-9 h-11" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#354c54] uppercase tracking-wider">Data de saída</Label>
              <div className="relative mt-1.5">
                <Calendar className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6b858e]" />
                <Input type="date" required value={form.dismissal} onChange={(e) => set('dismissal', e.target.value)} className="pl-9 h-11" />
              </div>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs font-semibold text-[#354c54] uppercase tracking-wider mb-2 block">Motivo da rescisão</Label>
              <RadioGroup value={form.reason} onValueChange={(v) => set('reason', v)} className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {[
                  { v: 'sem-justa-causa', label: 'Demissão sem justa causa' },
                  { v: 'pedido', label: 'Pedido de demissão' },
                  { v: 'acordo', label: 'Acordo (art. 484-A CLT)' },
                  { v: 'justa-causa', label: 'Demissão por justa causa' },
                  { v: 'termino-contrato', label: 'Fim de contrato por prazo determinado' },
                ].map(opt => (
                  <label key={opt.v} className={`flex items-center gap-3 p-3 rounded-[7px] border-2 cursor-pointer transition-all ${form.reason === opt.v ? 'border-[#2f5d65] bg-[#edf4f5]' : 'border-[#cbd8dc] hover:border-[#bacbd0]'}`}>
                    <RadioGroupItem value={opt.v} id={opt.v} />
                    <span className="text-sm font-medium text-[#243b42]">{opt.label}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>
            {form.reason === 'sem-justa-causa' && (
              <div className="md:col-span-2">
                <Label className="text-xs font-semibold text-[#354c54] uppercase tracking-wider mb-2 block">Aviso prévio</Label>
                <RadioGroup value={form.noticeType} onValueChange={(v) => set('noticeType', v)} className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {[
                    { v: 'indenizado', label: 'Indenizado (empresa paga)' },
                    { v: 'trabalhado', label: 'Trabalhado (você cumpriu)' },
                    { v: 'nenhum', label: 'Não recebi/não cumpri' },
                  ].map(opt => (
                    <label key={opt.v} className={`flex items-center gap-2 p-3 rounded-[7px] border-2 cursor-pointer transition-all ${form.noticeType === opt.v ? 'border-[#2f5d65] bg-[#edf4f5]' : 'border-[#cbd8dc] hover:border-[#bacbd0]'}`}>
                      <RadioGroupItem value={opt.v} id={`nt-${opt.v}`} />
                      <span className="text-sm text-[#243b42]">{opt.label}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>
            )}
            <div>
              <Label className="text-xs font-semibold text-[#354c54] uppercase tracking-wider">Férias vencidas não gozadas (dias)</Label>
              <Select value={form.vacationOverdue} onValueChange={(v) => set('vacationOverdue', v)}>
                <SelectTrigger className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[0, 5, 10, 15, 20, 30].map(d => <SelectItem key={d} value={String(d)}>{d} dias</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-3 mt-6 pt-6 border-t border-[#e1e9eb]">
            <Button type="submit" className="flex-1 h-12 bg-[#153e44] hover:bg-[#0c3036] text-white font-semibold text-base">
              <Calculator className="w-5 h-5 mr-2" /> Calcular minha rescisão
            </Button>
            <Button type="button" variant="outline" onClick={reset} className="h-12">
              <RotateCcw className="w-4 h-4 mr-2" /> Limpar
            </Button>
          </div>
        </form>

        {/* Result */}
        {result && !result.error && (
          <div id="resultado" className="mt-8 space-y-5">
            <div className="aj-edition bg-[#f7fafb] rounded-r-[10px] p-6 md:p-8">
              <div className="text-xs text-[#075a6d] font-semibold uppercase tracking-widest mb-2">Você deve receber</div>
              <div className="flex flex-wrap items-baseline gap-2">
                <div className="text-4xl md:text-6xl font-semibold text-[#122f36]">{formatBRL(result.total)}</div>
                <div className="text-sm text-[#425a62]">valor bruto estimado</div>
              </div>
              <div className="mt-4 pt-4 border-t border-[#cbd8dc] grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div><div className="text-[#425a62] text-xs">Modalidade</div><div className="font-semibold">{result.reasonLabel}</div></div>
                <div><div className="text-[#425a62] text-xs">Tempo trabalhado</div><div className="font-semibold">{result.yearsCompleted} anos ({result.daysWorked} dias)</div></div>
                <div><div className="text-[#425a62] text-xs">Verbas identificadas</div><div className="font-semibold">{result.items.length}</div></div>
              </div>
            </div>

            <div className="bg-white rounded-[10px] border border-[#cbd8dc] p-6">
              <h3 className="font-semibold text-[#153e44] text-lg mb-4">Composição das verbas</h3>
              <div className="divide-y divide-[#e1e9eb]">
                {result.items.map((item, i) => (
                  <div key={i} className="py-3 flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-[#153e44]">{item.label}</div>
                      <div className="text-xs text-[#52676e] mt-0.5">{item.detail}</div>
                    </div>
                    <div className="font-semibold text-[#153e44] whitespace-nowrap">{formatBRL(item.value)}</div>
                  </div>
                ))}
                <div className="pt-4 flex items-center justify-between">
                  <div className="font-semibold text-[#153e44]">Total bruto</div>
                  <div className="text-xl font-semibold text-[#075a6d]">{formatBRL(result.total)}</div>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="bg-amber-50 border border-amber-200 rounded-[10px] p-5">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                <div>
                  <div className="font-semibold text-amber-900 mb-2">Observações importantes</div>
                  <ul className="space-y-1.5 text-sm text-amber-900">
                    {result.notes.map((n, i) => <li key={i} className="flex gap-2"><span>•</span><span>{n}</span></li>)}
                  </ul>
                </div>
              </div>
            </div>

            {/* CTA lawyers */}
            <div className="bg-white rounded-[10px] border-2 border-[#cbd8dc] p-6 md:p-8">
              <div className="text-center">
                <div className="inline-flex items-center gap-2 bg-[#edf4f5] border border-[#cbd8dc] rounded-full px-3 py-1 text-xs font-medium text-[#153e44] mb-3">
                  <ShieldAlert className="w-3.5 h-3.5" /> Suspeita de valores incorretos?
                </div>
                <h3 className="text-xl md:text-2xl font-semibold text-[#153e44]">Fale com um advogado trabalhista</h3>
                <p className="text-[#425a62] mt-2 max-w-2xl mx-auto">Muitos trabalhadores recebem menos do que deveriam. Um advogado especializado analisa seu caso e pode cobrar diferenças com horas extras, adicionais e reflexos.</p>
              </div>
              {trabFirms.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
                  {trabFirms.map(f => <LawFirmCard key={f.id} firm={f} />)}
                </div>
              )}
              <div className="flex flex-wrap justify-center gap-3 mt-6">
                <Link to="/match"><Button className="bg-[#153e44] hover:bg-[#0c3036] h-11"><Sparkles className="w-4 h-4 mr-2" /> Match com IA</Button></Link>
                <Link to="/categoria/trabalhista"><Button variant="outline" className="h-11">Ver todos os trabalhistas <ChevronRight className="w-4 h-4 ml-1" /></Button></Link>
              </div>
            </div>
          </div>
        )}

        {result?.error && (
          <div className="mt-6 bg-red-50 border border-red-200 rounded-[7px] p-5 text-red-700 text-sm">{result.error}</div>
        )}

        {/* Info section (always visible) */}
        {!result && (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { icon: DollarSign, title: 'Verbas calculadas', desc: 'Saldo, aviso, 13º, férias, FGTS e multa — tudo em um clique.' },
              { icon: TrendingUp, title: 'Baseado na CLT', desc: 'Fórmulas atualizadas com a Reforma Trabalhista de 2017 e Lei 12.506/2011.' },
              { icon: Scale, title: 'Consulte um advogado', desc: 'Nosso simulador é uma estimativa. Um profissional pode identificar diferenças que dobram o valor.' },
            ].map((s, i) => (
              <div key={i} className="bg-white border border-[#cbd8dc] rounded-[7px] p-5">
                <div className="w-10 h-10 rounded-[7px] bg-[#153e44] flex items-center justify-center mb-3"><s.icon className="w-5 h-5 text-[#075a6d]" /></div>
                <div className="font-semibold text-[#153e44]">{s.title}</div>
                <p className="text-sm text-[#425a62] mt-1">{s.desc}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* FAQ */}
      <section className="bg-[#f2f6f7] py-14">
        <div className="aj-wrap max-w-[820px]">
          <h2 className="text-2xl md:text-3xl font-semibold text-[#153e44] mb-6">Perguntas frequentes sobre rescisão trabalhista</h2>
          <div className="space-y-3">
            {FAQS.map((f, i) => (
              <details key={i} className="group bg-white rounded-[7px] border border-[#cbd8dc] p-5 open:shadow-md">
                <summary className="font-semibold text-[#153e44] cursor-pointer list-none flex items-center justify-between gap-4">
                  {f.q}
                  <ChevronRight className="w-5 h-5 text-[#075a6d] transition-transform group-open:rotate-90 shrink-0" />
                </summary>
                <p className="mt-3 text-[#354c54] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
